import React, {useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import constants from 'helpers/constants';
import DialogContent from 'components/atoms/Dialogs/DialogContent/DialogContent';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import DialogFooter from 'components/molecules/Dialogs/DialogFooter/DialogFooter';
import Button from 'components/atoms/Buttons/Button/Button';
import DialogHeader from 'components/molecules/Dialogs/DialogHeader/DialogHeader';
import {Span} from 'components/atoms/Text/Typography/Typography';
import utils from 'helpers/utils';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Close from '@mui/icons-material/Close';
import Add from '@mui/icons-material/Add';
import StyledClientAddDialog from 'components/organisms/Dialogs/ClientAddDialog/ClientAddDialog.styles';

const ClientAddDialog = React.forwardRef((props, ref) => {
  const {
    onSubmit,
    ...innerProps
  } = useComponentProps(props, 'ClientAddDialog', {
    children: ['header', 'content', 'footer']
  });

  const innerRef = useRef(null);
  const formRef = useRef(null);

  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(null);
  const [validation, setValidation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useImperativeHandle(ref, () => innerRef.current);

  const fields = useMemo(() => {
    return [
      {
        name: 'name',
        label: 'Name',
        inlineLabel: 'name',
        entity: 'client',
        type: constants.formFieldTypes.text,
        validation: constants.formFieldValidationTypes.text,
        initial: '',
        required: true,
        FormFieldProps: {
          variant: 'staticLabel'
        },
      },
      {
        name: 'currency',
        label: 'Currency',
        inlineLabel: 'currency',
        entity: 'client',
        FormFieldProps: {
          variant: 'staticLabel'
        }
      },
      {
        name: 'plan',
        label: 'Plan',
        inlineLabel: 'plan',
        entity: 'client',
        type: constants.formFieldTypes.autocomplete,
        validation: constants.formFieldValidationTypes.text,
        conversion: constants.formFieldConversionTypes.value,
        options: 'plans',
        initial: '',
        required: true,
        FormFieldProps: {
          variant: 'staticLabel'
        },
      },
      {
        name: 'description',
        label: 'Description',
        inlineLabel: 'description',
        entity: 'client',
        type: constants.formFieldTypes.richText,
        validation: constants.formFieldValidationTypes.text,
        initial: '',
        required: false,
        FormFieldProps: {
          variant: 'staticLabel'
        }
      }
    ]
  }, []);

  const handleSubmit = (values, actions) => {
    setSubmitting(true);

    const client = {
      ...values,
      hasVersion2: true
    }

    utils.asPromise(onSubmit)(client)
      .then(() => {
        innerProps.onClose?.(null, 'saveButtonClick');
      })
      .catch(() => {
        setError('Creating client failed');
      })
      .finally(() => {
        actions.setSubmitting(false);
        setSubmitting(false);
      });
  };

  const handleValidating = (isValidating, isDirty, hasErrors) => {
    setDirty(isDirty);
    if (hasErrors) {
      setValidation('Please check if all fields have the correct values');
    } else {
      setValidation(null);
    }
  }

  const handleSubmitClick = () => {
    formRef.current?.submit();
  };

  const handleCancel = (e) => {
    innerRef.current?.close?.(e, 'cancelButtonClick');
  }

  const handleClose = (e, reason) => {
    if ((!submitting && !error && !dirty) || ['escapeKeyDown', 'closeButtonClick', 'cancelButtonClick'].includes(reason)) {
      innerProps.onClose?.(e, reason);
    }
  }

  const renderButtons = () => {
    return <React.Fragment>
      <Button children={'Cancel'}
              variant="text"
              startIcon={<Icon icon={Close}/>}
              onClick={handleCancel}/>
      <Button disabled={submitting}
              type="submit"
              color="success"
              variant="contained"
              children="Create client"
              startIcon={<Icon icon={Add}/>}
              onClick={handleSubmitClick}/>
    </React.Fragment>
  }

  return <StyledClientAddDialog ref={innerRef} {...innerProps} onClose={handleClose}>
    <DialogHeader className="ClientAddDialog-header"
                  title="Add client" />
    <DialogContent className="ClientAddDialog-content" >
      <InlineForm ref={formRef} fields={fields}
                  onValidating={handleValidating}
                  onSubmit={handleSubmit} />
    </DialogContent>
    <DialogFooter className="ClientAddDialog-footer"
                  info={(error ?? validation) ? <Span color="error">{error ?? validation}</Span> : null}
                  buttons={renderButtons()} />
  </StyledClientAddDialog>
});

ClientAddDialog.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  onSubmit: PropTypes.func
};

export default ClientAddDialog;
